import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageBody, StatusDot, EmptyState } from "@/components/ui-bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Copy, RefreshCw, Trash2, Download, Cloud, Package } from "lucide-react";

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "temperature", location: "", group_id: "" });
  const [viewKey, setViewKey] = useState(null); // device whose api key is shown

  const load = async () => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    const [d, g] = await Promise.all([
      api.get("/devices", { params }),
      api.get("/groups"),
    ]);
    setDevices(d.data);
    setGroups(g.data);
  };

  useEffect(() => { load(); }, [search, statusFilter]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = { ...form };
    if (!payload.group_id) delete payload.group_id;
    const { data } = await api.post("/devices", payload);
    toast.success("Device registered");
    setOpen(false);
    setForm({ name: "", type: "temperature", location: "", group_id: "" });
    setViewKey(data);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this device and its telemetry?")) return;
    await api.delete(`/devices/${id}`);
    toast.success("Device deleted");
    load();
  };

  const rotate = async (id) => {
    const { data } = await api.post(`/devices/${id}/rotate-key`);
    const dev = devices.find(d => d.device_id === id);
    setViewKey({ ...dev, api_key: data.api_key });
    toast.success("API key rotated");
    load();
  };

  const provision = async (id) => {
    try {
      const { data } = await api.post(`/devices/${id}/provision-aws`);
      toast.success(`AWS thing created: ${data.thing_name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Provisioning failed");
    }
  };

  const downloadBundle = async (id) => {
    try {
      const resp = await api.get(`/devices/${id}/bundle`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `device-${id}-bundle.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Bundle downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Provision the device first");
    }
  };

  const exportCsv = async (id) => {
    const resp = await api.get("/telemetry/export", { params: { device_id: id }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `telemetry-${id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="devices-page">
      <PageHeader
        eyebrow="// REGISTRY"
        title="Devices"
        description="Register and manage every sensor connected to your account."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="open-add-device-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#001E70]">
                <Plus className="w-4 h-4" /> Register device
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-neutral-900" data-testid="add-device-dialog">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">Register a new device</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="label-eyebrow text-neutral-500">Name</Label>
                  <Input data-testid="device-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Temp Probe 03" />
                </div>
                <div>
                  <Label className="label-eyebrow text-neutral-500">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="device-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["temperature","humidity","pressure","vibration","co2","generic"].map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-eyebrow text-neutral-500">Group</Label>
                  <Select value={form.group_id || "none"} onValueChange={(v) => setForm({ ...form, group_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="device-group-select"><SelectValue placeholder="Ungrouped" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Ungrouped</SelectItem>
                      {groups.map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-eyebrow text-neutral-500">Location</Label>
                  <Input data-testid="device-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Optional" />
                </div>
              </div>
              <DialogFooter>
                <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm hover:bg-neutral-100">Cancel</button>
                <button onClick={create} data-testid="confirm-add-device-btn" className="px-4 py-2 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#001E70]">Create</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <PageBody>
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search devices…" className="pl-9 rounded-sm" />
          </div>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="status-filter" className="w-40"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="online">Online</SelectItem>
              <SelectItem value="offline">Offline</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="alert">Alert</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {devices.length === 0 ? (
          <EmptyState title="No devices yet" body="Register your first sensor or seed demo data via the dashboard simulator." />
        ) : (
          <div className="border border-neutral-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr className="text-left label-eyebrow text-neutral-500">
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Group</th>
                  <th className="px-4 py-3">Last seen</th>
                  <th className="px-4 py-3">AWS Thing</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.device_id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`device-row-${d.device_id}`}>
                    <td className="px-4 py-3"><StatusDot status={d.status} /> <span className="ml-2 font-mono text-xs text-neutral-600">{d.status}</span></td>
                    <td className="px-4 py-3 font-medium">{d.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{d.type}</td>
                    <td className="px-4 py-3 text-neutral-600">{d.location || "—"}</td>
                    <td className="px-4 py-3 text-neutral-600">{groups.find(g => g.group_id === d.group_id)?.name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-neutral-600">{d.last_seen ? new Date(d.last_seen).toLocaleString() : "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{d.aws_thing_name || <span className="text-neutral-400">not provisioned</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setViewKey(d)} data-testid={`view-key-${d.device_id}`} title="View API key" className="p-2 hover:bg-neutral-100"><Copy className="w-4 h-4" /></button>
                        <button onClick={() => rotate(d.device_id)} data-testid={`rotate-${d.device_id}`} title="Rotate key" className="p-2 hover:bg-neutral-100"><RefreshCw className="w-4 h-4" /></button>
                        <button onClick={() => provision(d.device_id)} data-testid={`provision-${d.device_id}`} title="Provision AWS IoT" className="p-2 hover:bg-neutral-100"><Cloud className="w-4 h-4" /></button>
                        {d.aws_thing_name && (
                          <button onClick={() => downloadBundle(d.device_id)} data-testid={`bundle-${d.device_id}`} title="Download device bundle (cert + key + sample)" className="p-2 hover:bg-neutral-100"><Package className="w-4 h-4" /></button>
                        )}
                        <button onClick={() => exportCsv(d.device_id)} data-testid={`export-${d.device_id}`} title="Export CSV" className="p-2 hover:bg-neutral-100"><Download className="w-4 h-4" /></button>
                        <button onClick={() => remove(d.device_id)} data-testid={`delete-${d.device_id}`} title="Delete" className="p-2 hover:bg-neutral-100 text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageBody>

      {/* View API key dialog */}
      <Dialog open={!!viewKey} onOpenChange={(v) => !v && setViewKey(null)}>
        <DialogContent className="rounded-none border-neutral-900" data-testid="apikey-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Device API key</DialogTitle></DialogHeader>
          {viewKey && (
            <div>
              <div className="text-sm text-neutral-600">Use this key in the <code>api_key</code> field when POSTing to <code>/api/telemetry/ingest</code>.</div>
              <div className="mt-4 border border-neutral-900 bg-neutral-50 p-3 font-mono text-sm break-all" data-testid="apikey-value">{viewKey.api_key}</div>
              <button
                onClick={() => { navigator.clipboard.writeText(viewKey.api_key); toast.success("Copied"); }}
                data-testid="copy-apikey-btn"
                className="mt-3 inline-flex items-center gap-2 px-3 py-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white text-sm"
              ><Copy className="w-4 h-4" /> Copy to clipboard</button>
              <pre className="mt-5 bg-neutral-900 text-white text-xs p-3 overflow-x-auto"><code>{`curl -X POST $API/api/telemetry/ingest \\
  -H "Content-Type: application/json" \\
  -d '{"api_key":"${viewKey.api_key}","metric":"temperature","value":21.3,"unit":"°C"}'`}</code></pre>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
